/* ============================================================================
 * tp-pn-search.js  —  Trioprint OEM-Nummern-Suche (dependency-free)
 * ----------------------------------------------------------------------------
 * Deterministischer Matcher für Druckerpatronen-/Toner-Artikelnummern.
 * Kein Fuzzy-Matching (würde TN2420->TN2410 ziehen). Exakt > Prefix.
 *
 * Doc-Schema (eine Zeile pro Produkt im Index-JSON):
 *   {
 *     h:  "handle",                  // Shopify product handle
 *     t:  "Voller Produkttitel",     // für Rendering + soft descriptor match
 *     p:  2699,                      // Preis in Cent
 *     c:  null | 3499,               // compare_at in Cent (oder null)
 *     img:"https://cdn.../x.webp",   // Bild-URL
 *     av: true,                      // verfügbar
 *     b:  "HP",                      // Hersteller-Anzeige
 *     nb: ["hp"],                    // normalisierte Brand-Tokens (Filter)
 *     nk: ["303","t6n02ae",...]      // normalisierte OEM-Keys (squashed)
 *   }
 *
 * WICHTIG: k-Keys werden beim Index-Build (Python) mit DERSELBEN
 * Normalisierung erzeugt wie hier die Query (squash()). Sonst kein Match.
 * ==========================================================================*/

(function (global) {
  'use strict';

  // Herstellernamen, die der Kunde TIPPT (nicht Serien-Präfixe wie TN/CF/TK!)
  const BRANDS = [
    'hp','hewlett','packard','brother','canon','epson','kyocera','lexmark',
    'samsung','oki','xerox','ricoh','dell','utax','triumphadler','triumph',
    'adler','olivetti','pantum','sharp','konica','minolta','develop','toshiba',
    'panasonic','tally','genicom'
  ];
  const BRAND_SET = {};
  BRANDS.forEach(function (b) { BRAND_SET[b] = true; });

  // Suffix-Modifier, die an eine Nummer ANGEHÄNGT werden ("305 xl" -> "305xl")
  const MODIFIERS = { 'xl': 1, 'xxl': 1, 'xxxl': 1, 'xs': 1, 's': 1, 'l': 1, 'plus': 1 };

  // Beschreibende Wörter, die KEINE Artikelnummer sind -> beim Core ignorieren
  const DESCRIPTORS = {};
  ['toner','patrone','patronen','tinte','tintenpatrone','tintenpatronen',
   'druckerpatrone','druckerpatronen','tintenflasche','tintenflaschen',
   'trommel','bildtrommel','drum','trommeleinheit','resttoner','resttonerbehaelter',
   'resttonerbehälter','waste','druckkopf','printhead','papier','set','multipack',
   'doppelpack','sparset','vorteilspack','original','originale','kompatibel',
   'kompatible','rebuilt','refill','schwarz','black','bk','cyan','magenta','gelb',
   'yellow','color','colour','farbe','foto','photo','fuer','für','und','der','die',
   'das','mit','von','x','stueck','stück','rot','blau','grün','gruen','weiss','weiß',
   'mehrfarbig','farbig','tricolor'
  ].forEach(function (d) { DESCRIPTORS[d] = true; });

  /* Kanonische Normalisierung — MUSS bit-identisch zur Python-Index-Seite sein */
  function squash(s) {
    return String(s).toLowerCase().replace(/[\s\-\/_.,()]+/g, '');
  }

  function cleanToken(t) {
    return String(t).toLowerCase().replace(/[^a-z0-9+]/g, '');
  }

  /* Zerlegt die Roh-Query in { brand, cands[], isPN } -------------------- */
  function parseQuery(raw) {
    const tokens = String(raw).toLowerCase().split(/\s+/)
      .map(cleanToken).filter(Boolean);

    let brand = null;
    let rest = [];      // Tokens, die zur Nummer gehören (inkl. Serien-Präfix)
    let i, t;

    for (i = 0; i < tokens.length; i++) {
      t = tokens[i];
      if (brand === null && BRAND_SET[t]) { brand = t; continue; }
      if (DESCRIPTORS[t]) continue;       // Beschreibung raus
      rest.push(t);
    }

    // Modifier-Merge: einzelnstehendes "xl" an vorheriges Nummern-Token kleben
    let merged = [];
    for (i = 0; i < rest.length; i++) {
      if (MODIFIERS[rest[i]] && merged.length && /\d/.test(merged[merged.length - 1])) {
        merged[merged.length - 1] += rest[i];
      } else {
        merged.push(rest[i]);
      }
    }

    let core = merged.join('');            // alles zusammenkleben -> "tn2420", "305xl"
    let cands = {};
    if (core) cands[core] = 1;

    // Geklebter Marken-Präfix ("hp59a" -> auch "59a", brand=hp)
    if (brand === null) {
      for (i = 0; i < BRANDS.length; i++) {
        let b = BRANDS[i];
        if (core.length > b.length && core.indexOf(b) === 0) {
          let stripped = core.slice(b.length);
          if (/\d/.test(stripped)) { cands[stripped] = 1; brand = b; }
        }
      }
    }

    let candList = Object.keys(cands);

    // "Wort"-Token = reines Alpha, >=4 Zeichen, kein Modifier -> natürliche Sprache.
    // 3-Buchstaben-Serienpräfixe (CLI, PGI, TZe) zählen NICHT als Wort.
    let hasWord = false;
    for (i = 0; i < rest.length; i++) {
      if (rest[i].length >= 4 && /^[a-z]+$/.test(rest[i]) && !MODIFIERS[rest[i]]) {
        hasWord = true; break;
      }
    }

    let isPN = false;
    if (!hasWord) {
      for (i = 0; i < candList.length; i++) {
        let cand = candList[i];
        if (/\d/.test(cand) && cand.length >= 2 && cand.length <= 16) { isPN = true; break; }
      }
    }
    return { brand: brand, cands: candList, isPN: isPN };
  }

  /* Index-Aufbau: Map key -> [docIndex...] + Liste aller Keys für Prefix --- */
  function buildIndex(docs) {
    let exact = Object.create(null);
    let allKeys = [];
    for (let d = 0; d < docs.length; d++) {
      let ks = docs[d].nk || [];
      for (let j = 0; j < ks.length; j++) {
        let key = ks[j];
        if (!exact[key]) { exact[key] = []; allKeys.push(key); }
        exact[key].push(d);
      }
    }
    return { docs: docs, exact: exact, keys: allKeys };
  }

  /* Suche -> sortierte Doc-Liste mit Score ------------------------------- */
  function search(idx, raw) {
    let q = parseQuery(raw);
    if (!q.isPN) return { isPN: false, brand: q.brand, results: [] };

    let scores = Object.create(null);   // docIndex -> bestScore
    let i, c, d, arr;

    for (i = 0; i < q.cands.length; i++) {
      c = q.cands[i];
      if (c.length < 2) continue;

      // exakt
      arr = idx.exact[c];
      if (arr) for (let a = 0; a < arr.length; a++) {
        d = arr[a];
        if ((scores[d] || 0) < 100) scores[d] = 100;
      }

      // prefix (key startsWith cand) — nur wenn cand lang genug
      if (c.length >= 3) {
        for (let ki = 0; ki < idx.keys.length; ki++) {
          let key = idx.keys[ki];
          if (key.length > c.length && key.indexOf(c) === 0) {
            // näher dran = besser (weniger Extra-Zeichen)
            let sc = 60 - Math.min(20, key.length - c.length);
            arr = idx.exact[key];
            for (let b2 = 0; b2 < arr.length; b2++) {
              d = arr[b2];
              if ((scores[d] || 0) < sc) scores[d] = sc;
            }
          }
        }
      }
    }

    let hits = Object.keys(scores).map(function (d) {
      return { d: +d, score: scores[d] };
    });

    // Marken-Filter: nur anwenden, wenn dadurch nicht alles wegfällt
    if (q.brand) {
      let withBrand = hits.filter(function (h) {
        const bs = idx.docs[h.d].nb || [];
        return bs.indexOf(q.brand) !== -1;
      });
      if (withBrand.length) hits = withBrand;
    }

    hits.sort(function (x, y) {
      if (y.score !== x.score) return y.score - x.score;
      // Tie-break: verfügbare zuerst, dann Titel
      const dx = idx.docs[x.d], dy = idx.docs[y.d];
      if (!!dy.av !== !!dx.av) return dy.av ? 1 : -1;
      return (dx.t || '').localeCompare(dy.t || '');
    });

    return {
      isPN: true,
      brand: q.brand,
      results: hits.map(function (h) {
        return { doc: idx.docs[h.d], score: h.score };
      })
    };
  }

  const API = { squash: squash, parseQuery: parseQuery, buildIndex: buildIndex, search: search };

  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  else global.TPSearch = API;

})(typeof window !== 'undefined' ? window : this);
